/**
 * Single source of truth for which files may be attached to an order.
 *
 * Shared by the storage bucket allowlist (migration), the picker `accept`
 * attribute, upload validation, and the stored MIME resolution. Widened
 * (2026-07) from an images/3D-only list that SILENTLY rejected PDFs and Excel
 * files: podiatrists attached business documents that never reached storage and
 * saw no error. See docs / PR for the investigation.
 */

/** Max attachment size (bytes). 100 MB, matching the scans bucket limit. */
export const DEFAULT_MAX_BYTES = 100 * 1024 * 1024;

/** Concrete MIME types allowed for order attachments (kept in sync with the
 *  scans bucket's allowed_mime_types). `application/octet-stream` is the
 *  catch-all many STL/3D and zipped office files arrive as. */
export const ORDER_FILE_MIME: readonly string[] = [
  // images
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  // 3D scans (STL frequently arrives as octet-stream)
  'model/stl',
  'application/sla',
  'model/obj',
  'model/ply',
  'application/octet-stream',
  // documents
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-excel', // xls
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/msword', // doc
  'application/zip',
  'application/x-zip-compressed',
];

/** Allowed lowercase file extensions. Extension is the PRIMARY validation gate
 *  (a browser-reported MIME type is spoofable and sometimes empty). */
export const ORDER_FILE_EXT: readonly string[] = [
  'stl',
  'obj',
  'ply',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'gif',
  'pdf',
  'xlsx',
  'xls',
  'csv',
  'doc',
  'docx',
  'zip',
];

/** Value for a file input `accept` attribute (extensions + concrete MIME
 *  types). octet-stream is omitted so the picker does not advertise "any
 *  binary". */
export const ORDER_FILE_ACCEPT = [
  ...ORDER_FILE_EXT.map((e) => `.${e}`),
  ...ORDER_FILE_MIME.filter((m) => m !== 'application/octet-stream'),
].join(',');

const EXT_TO_MIME: Record<string, string> = {
  stl: 'model/stl',
  obj: 'model/obj',
  ply: 'model/ply',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  zip: 'application/zip',
};

/** Lowercase extension of a filename ('' if none). */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Resolve a trustworthy content type for storage. Prefer a concrete
 * browser-reported type; otherwise (empty or generic octet-stream) derive it
 * from the extension, so PDFs/Excel are stored with a real MIME the draft email
 * and the file list can rely on.
 */
export function resolveOrderFileMime(file: { name: string; type: string }): string {
  if (file.type && file.type !== '' && file.type !== 'application/octet-stream') return file.type;
  return EXT_TO_MIME[fileExtension(file.name)] ?? 'application/octet-stream';
}

export type OrderFileValidation = { ok: true } | { ok: false; reason: string };

/**
 * Validate a candidate order attachment. Rejects (NEVER silently) anything
 * without an allowed extension, whose concrete MIME type contradicts the
 * allowlist, or over the size limit. The extension is the primary gate, which
 * closes the empty-content-type bypass (a typeless / mislabeled file whose
 * extension is not allowed is rejected).
 */
export function validateOrderFile(
  file: { name: string; type: string; size: number },
  maxBytes: number = DEFAULT_MAX_BYTES,
): OrderFileValidation {
  const ext = fileExtension(file.name);
  if (!ORDER_FILE_EXT.includes(ext)) {
    return {
      ok: false,
      reason: `Unsupported file type "${ext || file.name}". Allowed: PDF, Excel, CSV, Word, images (JPG/PNG/WEBP/GIF), STL/OBJ/PLY, ZIP.`,
    };
  }
  if (
    file.type &&
    file.type !== 'application/octet-stream' &&
    !ORDER_FILE_MIME.includes(file.type)
  ) {
    return {
      ok: false,
      reason: `File content type "${file.type}" is not an allowed order-document type.`,
    };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      reason: `File is ${(file.size / 1048576).toFixed(1)} MB; the limit is ${(
        maxBytes / 1048576
      ).toFixed(0)} MB.`,
    };
  }
  return { ok: true };
}
